import { useEffect, useState, useCallback } from "react";
import {
  Megaphone, UsersThree, ChatCircleDots, Pulse, Plus, Trash, PaperPlaneRight,
  Envelope, Phone, Ticket, Copy, Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, CoverPhoto } from "@/components/Avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LiveFeed from "@/pages/LiveFeed";

const KIND_TONE = {
  stock: "text-[#002FA7]", order: "text-[#0B7A5B]", request: "text-[#8A6A00]",
  tool: "text-[#002FA7]", idea: "text-[#0B7A5B]", scrap: "text-[#FF3B30]",
  feedback: "text-slate-500",
};

export default function Network() {
  const { user } = useAuth();
  const [tab, setTab] = useState("directory");
  const [directory, setDirectory] = useState({ people: [], organisations: [] });
  const [search, setSearch] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [activity, setActivity] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [invites, setInvites] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", body: "", audience: "all", pinned: false });

  const loadDirectory = useCallback(async () => {
    const { data } = await api.get("/network/directory", { params: { search } });
    setDirectory(data);
  }, [search]);

  const load = useCallback(async () => {
    const [a, act, th] = await Promise.all([
      api.get("/announcements"),
      api.get("/activity"),
      api.get("/messages/threads"),
    ]);
    setAnnouncements(a.data);
    setActivity(act.data);
    setThreads(th.data);
    if (user?.role === "admin") {
      try {
        setInvites((await api.get("/invites")).data);
      } catch {}
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const openChat = async (person) => {
    setActiveChat(person);
    const { data } = await api.get(`/messages/${person.id}`);
    setMessages(data);
    load();
  };

  useEffect(() => {
    if (!activeChat) return;
    const t = setInterval(async () => {
      const { data } = await api.get(`/messages/${activeChat.id}`);
      setMessages(data);
    }, 12000);
    return () => clearInterval(t);
  }, [activeChat]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await api.post("/messages", { to_user_id: activeChat.id, body: draft });
      setDraft("");
      const { data } = await api.get(`/messages/${activeChat.id}`);
      setMessages(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const postAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", annForm);
      toast.success("Announcement posted");
      setAnnOpen(false);
      setAnnForm({ title: "", body: "", audience: "all", pinned: false });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeAnnouncement = async (id) => {
    await api.delete(`/announcements/${id}`);
    toast.success("Announcement removed");
    load();
  };

  const makeInvite = async (role) => {
    try {
      const { data } = await api.post("/invites", { role });
      toast.success(`Invite code ${data.code} created`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const totalUnread = threads.reduce((s, t) => s + t.unread, 0);

  return (
    <div data-testid="network-page">
      <PageHeader
        overline="Everyone connected"
        title="Network"
        description="Your whole floor in one place — people, partners, announcements, direct messages and a live activity feed."
        action={
          user?.role === "admin" ? (
            <Button
              data-testid="add-announcement-button"
              onClick={() => setAnnOpen(true)}
              className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
            >
              <Plus size={16} className="mr-2" /> Post announcement
            </Button>
          ) : null
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="network-tab-directory" value="directory" className="rounded-none text-xs uppercase tracking-wider">
            <UsersThree size={14} className="mr-1.5" /> Directory
          </TabsTrigger>
          <TabsTrigger data-testid="network-tab-announcements" value="announcements" className="rounded-none text-xs uppercase tracking-wider">
            <Megaphone size={14} className="mr-1.5" /> Notices ({announcements.length})
          </TabsTrigger>
          <TabsTrigger data-testid="network-tab-messages" value="messages" className="rounded-none text-xs uppercase tracking-wider">
            <ChatCircleDots size={14} className="mr-1.5" /> Messages{totalUnread ? ` (${totalUnread})` : ""}
          </TabsTrigger>
          <TabsTrigger data-testid="network-tab-live" value="live" className="rounded-none text-xs uppercase tracking-wider">
            <Camera size={14} className="mr-1.5" /> Live feed
          </TabsTrigger>
          <TabsTrigger data-testid="network-tab-activity" value="activity" className="rounded-none text-xs uppercase tracking-wider">
            <Pulse size={14} className="mr-1.5" /> Activity
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "live" && (
        <div data-testid="network-live-panel">
          <LiveFeed embedded />
        </div>
      )}

      {tab === "directory" && (
        <div data-testid="directory-panel">
          <Input
            data-testid="directory-search-input"
            placeholder="Search people, companies, emails…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4 rounded-none border-slate-300 sm:max-w-md"
          />
          <div className="overline mb-2 text-slate-500">People ({directory.people.length})</div>
          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {directory.people.map((p) => (
              <div key={p.id} data-testid={`person-card-${p.id}`} className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <Avatar imageId={p.avatar_id} name={p.name} size="md" testid={`person-avatar-${p.id}`} />
                    <div className="min-w-0">
                    <div className="font-display truncate text-base font-semibold text-[#0F172A]">
                      {p.name} {p.is_self && <span className="text-xs text-slate-400">(you)</span>}
                    </div>
                    <div className="mt-1.5"><StatusBadge value={p.role} /></div>
                    </div>
                  </div>
                  {!p.is_self && (
                    <button
                      data-testid={`message-person-${p.id}`}
                      onClick={() => { setTab("messages"); openChat(p); }}
                      title="Send a message"
                      className="border border-border p-2 text-[#002FA7] transition-colors duration-150 hover:bg-[#EEF2FF]"
                    >
                      <ChatCircleDots size={15} />
                    </button>
                  )}
                </div>
                <div className="mt-3 space-y-1 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5 truncate"><Envelope size={13} /> {p.email}</div>
                  {p.company && <div>{p.company}</div>}
                  {(p.city || p.country) && <div>{[p.city, p.country].filter(Boolean).join(", ")}</div>}
                </div>
              </div>
            ))}
          </div>

          <div className="overline mb-2 text-slate-500">
            Organisations ({directory.organisations.length})
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {directory.organisations.map((o) => (
              <div key={o.id} data-testid={`org-card-${o.id}`} className="border border-border bg-white">
                <CoverPhoto imageId={o.cover_id} name={o.name} testid={`org-cover-${o.id}`} className="h-20" />
                <div className="p-4">
                <div className="-mt-8 mb-2"><Avatar imageId={o.logo_id} name={o.name} size="md" testid={`org-logo-${o.id}`} className="border-2 border-white" /></div>
                <div className="font-display text-base font-semibold text-[#0F172A]">{o.name}</div>
                <div className="mt-1.5"><StatusBadge value={o.type} /></div>
                <div className="mt-3 space-y-1 text-xs text-slate-500">
                  {o.contact_person && <div className="font-medium text-[#0F172A]">{o.contact_person}</div>}
                  {o.email && <div className="flex items-center gap-1.5 truncate"><Envelope size={13} /> {o.email}</div>}
                  {o.phone && <div className="flex items-center gap-1.5"><Phone size={13} /> {o.phone}</div>}
                </div>
                </div>
              </div>
            ))}
          </div>

          {user?.role === "admin" && (
            <div className="mt-6 border border-border bg-white p-4" data-testid="invites-panel">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="overline text-slate-500">Invite people in</span>
                <div className="flex flex-wrap gap-2">
                  {["member", "vendor", "buyer"].map((r) => (
                    <button
                      key={r}
                      data-testid={`create-invite-${r}`}
                      onClick={() => makeInvite(r)}
                      className="flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#002FA7] hover:bg-[#EEF2FF]"
                    >
                      <Ticket size={13} /> {r} code
                    </button>
                  ))}
                </div>
              </div>
              {invites.length === 0 ? (
                <p className="text-xs text-slate-500">No invite codes yet. Create one and share it — the person enters it on the sign-up page.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {invites.map((i) => (
                    <li key={i.id} data-testid={`invite-${i.id}`} className="flex items-center justify-between gap-2 py-2">
                      <span className="font-mono text-sm font-bold text-[#0F172A]">{i.code}</span>
                      <StatusBadge value={i.role} />
                      <span className="text-xs text-slate-400">{i.used_by ? "used" : "unused"}</span>
                      <button
                        data-testid={`copy-invite-${i.id}`}
                        onClick={() => { navigator.clipboard?.writeText(i.code)?.catch(() => {}); toast.success("Code copied"); }}
                        className="border border-border p-1.5 text-slate-600 hover:bg-slate-100"
                      >
                        <Copy size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "announcements" && (
        <div data-testid="announcements-panel">
          {announcements.length === 0 ? (
            <EmptyState testid="announcements-empty" title="No notices yet" description="Admins can post announcements everyone sees." />
          ) : (
            <div className="space-y-3">
              {announcements.map((a) => (
                <div
                  key={a.id}
                  data-testid={`announcement-${a.id}`}
                  className={`border bg-white p-4 ${a.pinned ? "border-l-4 border-l-[#002FA7] border-border" : "border-border"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-base font-semibold text-[#0F172A]">{a.title}</h3>
                        {a.pinned && <StatusBadge value="approved" />}
                        <StatusBadge value={a.audience} />
                      </div>
                      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{a.body}</p>
                      <div className="mt-2 text-xs text-slate-400">
                        {a.author_name} · {(a.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                    {user?.role === "admin" && (
                      <button
                        data-testid={`delete-announcement-${a.id}`}
                        onClick={() => removeAnnouncement(a.id)}
                        className="shrink-0 border border-border p-1.5 text-[#FF3B30] hover:bg-[#FFECEB]"
                      >
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "messages" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="messages-panel">
          <div className="border border-border bg-white lg:col-span-1">
            <div className="overline border-b border-border px-3 py-2.5 text-slate-500">Conversations</div>
            {threads.length === 0 ? (
              <p className="p-4 text-xs text-slate-500">
                No conversations yet. Open the Directory and tap the chat icon next to someone.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {threads.map((t) => (
                  <li key={t.user_id}>
                    <button
                      data-testid={`thread-${t.user_id}`}
                      onClick={() => openChat({ id: t.user_id, name: t.name })}
                      className={`w-full px-3 py-3 text-left transition-colors duration-150 hover:bg-slate-50 ${
                        activeChat?.id === t.user_id ? "bg-[#EEF2FF]" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-[#0F172A]">{t.name}</span>
                        {t.unread > 0 && (
                          <span data-testid={`unread-${t.user_id}`} className="bg-[#FF3B30] px-1.5 text-[0.65rem] font-bold text-white">
                            {t.unread}
                          </span>
                        )}
                      </div>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{t.last_message}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex min-h-[24rem] flex-col border border-border bg-white lg:col-span-2">
            {!activeChat ? (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-400">
                Pick a conversation, or message someone from the Directory.
              </div>
            ) : (
              <>
                <div className="border-b border-border px-4 py-2.5">
                  <span className="font-display text-sm font-bold text-[#0F172A]">{activeChat.name}</span>
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="message-list">
                  {messages.map((m) => {
                    const mine = m.from_user_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          data-testid={`message-${m.id}`}
                          className={`max-w-[80%] border px-3 py-2 text-sm ${
                            mine
                              ? "border-[#002FA7] bg-[#002FA7] text-white"
                              : "border-border bg-slate-50 text-[#0F172A]"
                          }`}
                        >
                          {m.body}
                          <span className={`mt-1 block text-[0.65rem] ${mine ? "text-white/70" : "text-slate-400"}`}>
                            {(m.created_at || "").slice(11, 16)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="flex gap-2 border-t border-border p-3" data-testid="message-form">
                  <Input
                    data-testid="message-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Write a message…"
                    className="rounded-none border-slate-300"
                  />
                  <Button
                    data-testid="send-message-button"
                    type="submit"
                    className="h-10 shrink-0 rounded-none bg-[#002FA7] px-4 hover:bg-[#00227A]"
                  >
                    <PaperPlaneRight size={16} />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "activity" && (
        <div className="border border-border bg-white" data-testid="activity-panel">
          {activity.length === 0 ? (
            <EmptyState testid="activity-empty" title="Nothing yet" description="Activity from across the floor will appear here." />
          ) : (
            <ul className="divide-y divide-border">
              {activity.map((e, i) => (
                <li key={i} data-testid={`activity-${i}`} className="flex items-start gap-3 p-3">
                  <span className={`overline mt-0.5 w-16 shrink-0 ${KIND_TONE[e.kind] || "text-slate-400"}`}>
                    {e.kind}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[#0F172A]">{e.title}</div>
                    <div className="text-xs text-slate-500">
                      {e.detail}
                      {e.who ? ` · ${e.who}` : ""}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-[0.7rem] text-slate-400">
                    {(e.at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Post announcement</DialogTitle>
            <DialogDescription>Everyone in the chosen audience sees this.</DialogDescription>
          </DialogHeader>
          <form onSubmit={postAnnouncement} className="space-y-4" data-testid="add-announcement-form">
            <div>
              <Label className="overline text-slate-500">Title</Label>
              <Input data-testid="announcement-title-input" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Message</Label>
              <Textarea data-testid="announcement-body-input" rows={4} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Audience</Label>
                <Select value={annForm.audience} onValueChange={(v) => setAnnForm({ ...annForm, audience: v })}>
                  <SelectTrigger data-testid="announcement-audience-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    <SelectItem value="team">Team only</SelectItem>
                    <SelectItem value="vendors">Vendors</SelectItem>
                    <SelectItem value="buyers">Buyers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Pin to top</Label>
                <Select value={annForm.pinned ? "yes" : "no"} onValueChange={(v) => setAnnForm({ ...annForm, pinned: v === "yes" })}>
                  <SelectTrigger data-testid="announcement-pinned-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-announcement-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Post
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
